import {
  FileText,
  Scale,
  AlertTriangle,
  Shield,
  Globe,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Info,
} from "lucide-react";
import "./Terms.css";

const Terms = () => {
  const lastUpdated = "December 15, 2024";

  return (
    <div className="terms-page">
      <div className="terms-header">
        <div className="header-content">
          <div className="header-icon">
            <FileText size={48} />
          </div>
          <div className="header-text">
            <h1>Terms & Conditions</h1>
            <p>
              Please read these terms and conditions carefully before using our
              service.
            </p>
            <div className="last-updated">
              <Calendar size={16} />
              <span>Last updated: {lastUpdated}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="terms-content">
        <div className="terms-nav">
          <h3>Quick Navigation</h3>
          <ul>
            <li>
              <a href="#acceptance">Acceptance of Terms</a>
            </li>
            <li>
              <a href="#description">Service Description</a>
            </li>
            <li>
              <a href="#user-accounts">User Accounts</a>
            </li>
            <li>
              <a href="#acceptable-use">Acceptable Use</a>
            </li>
            <li>
              <a href="#intellectual-property">Intellectual Property</a>
            </li>
            <li>
              <a href="#user-content">User Content</a>
            </li>
            <li>
              <a href="#payment-terms">Payment Terms</a>
            </li>
            <li>
              <a href="#privacy">Privacy</a>
            </li>
            <li>
              <a href="#disclaimers">Disclaimers</a>
            </li>
            <li>
              <a href="#limitation-liability">Limitation of Liability</a>
            </li>
            <li>
              <a href="#termination">Termination</a>
            </li>
            <li>
              <a href="#governing-law">Governing Law</a>
            </li>
            <li>
              <a href="#contact">Contact Information</a>
            </li>
          </ul>
        </div>

        <div className="terms-sections">
          {/* Introduction */}
          <section className="terms-section">
            <div className="section-icon">
              <Info size={24} />
            </div>
            <div className="section-content">
              <h2>Introduction</h2>
              <p>
                Welcome to ProSkillNest ("we," "our," or "us"). These Terms and
                Conditions ("Terms") govern your use of our website and
                services. By accessing or using our service, you agree to be
                bound by these Terms.
              </p>
              <div className="important-notice">
                <AlertTriangle size={20} />
                <div>
                  <strong>Important:</strong> If you disagree with any part of
                  these terms, then you may not access the service.
                </div>
              </div>
            </div>
          </section>

          {/* Acceptance of Terms */}
          <section id="acceptance" className="terms-section">
            <div className="section-icon">
              <CheckCircle size={24} />
            </div>
            <div className="section-content">
              <h2>Acceptance of Terms</h2>
              <p>
                By accessing and using ProSkillNest, you accept and agree to be
                bound by the terms and provision of this agreement.
                Additionally, when using ProSkillNest's services, you shall be
                subject to any posted guidelines or rules applicable to such
                services.
              </p>

              <div className="acceptance-conditions">
                <h3>By using our service, you confirm that:</h3>
                <ul>
                  <li>You are at least 13 years of age</li>
                  <li>You have the legal capacity to enter into these Terms</li>
                  <li>
                    You will comply with all applicable laws and regulations
                  </li>
                  <li>
                    You will not use the service for any unlawful purposes
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Service Description */}
          <section id="description" className="terms-section">
            <div className="section-icon">
              <Globe size={24} />
            </div>
            <div className="section-content">
              <h2>Service Description</h2>
              <p>
                ProSkillNest is an online learning platform that provides skill
                assessments, learning paths, and educational content to help
                users develop and improve their professional skills.
              </p>

              <div className="service-features">
                <h3>Our services include:</h3>
                <div className="features-grid">
                  <div className="feature">
                    <h4>Skill Assessments</h4>
                    <p>
                      Interactive assessments to evaluate your current skill
                      levels
                    </p>
                  </div>
                  <div className="feature">
                    <h4>Learning Paths</h4>
                    <p>Structured learning journeys tailored to your goals</p>
                  </div>
                  <div className="feature">
                    <h4>Progress Tracking</h4>
                    <p>Detailed analytics and progress monitoring tools</p>
                  </div>
                  <div className="feature">
                    <h4>Certificates</h4>
                    <p>
                      Recognition for completed assessments and learning paths
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* User Accounts */}
          <section id="user-accounts" className="terms-section">
            <div className="section-icon">
              <Shield size={24} />
            </div>
            <div className="section-content">
              <h2>User Accounts</h2>
              <p>
                To access certain features of our service, you may be required
                to create an account. You are responsible for maintaining the
                confidentiality of your account information.
              </p>

              <div className="account-responsibilities">
                <h3>Account Responsibilities:</h3>
                <ul>
                  <li>
                    Provide accurate and complete information during
                    registration
                  </li>
                  <li>Maintain the security of your password and account</li>
                  <li>
                    Notify us immediately of any unauthorized use of your
                    account
                  </li>
                  <li>
                    Accept responsibility for all activities under your account
                  </li>
                  <li>Not share your account credentials with others</li>
                </ul>
              </div>

              <div className="account-termination">
                <h3>Account Termination:</h3>
                <p>
                  We reserve the right to terminate or suspend accounts that
                  violate these Terms or engage in activities that may harm our
                  service or other users.
                </p>
              </div>
            </div>
          </section>

          {/* Acceptable Use */}
          <section id="acceptable-use" className="terms-section">
            <div className="section-icon">
              <Scale size={24} />
            </div>
            <div className="section-content">
              <h2>Acceptable Use Policy</h2>
              <p>
                You agree to use our service only for lawful purposes and in
                accordance with these Terms.
              </p>

              <div className="use-policies">
                <div className="allowed-uses">
                  <h3>✅ Allowed Uses:</h3>
                  <ul>
                    <li>Taking assessments for skill evaluation</li>
                    <li>Accessing learning materials and resources</li>
                    <li>Tracking your learning progress</li>
                    <li>Sharing achievements and certificates</li>
                    <li>Providing feedback to improve our services</li>
                  </ul>
                </div>

                <div className="prohibited-uses">
                  <h3>❌ Prohibited Uses:</h3>
                  <ul>
                    <li>
                      Attempting to gain unauthorized access to our systems
                    </li>
                    <li>Sharing assessment answers or cheating</li>
                    <li>Uploading malicious content or viruses</li>
                    <li>Harassing or threatening other users</li>
                    <li>
                      Using the service for commercial purposes without
                      permission
                    </li>
                    <li>Reverse engineering or copying our content</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Intellectual Property */}
          <section id="intellectual-property" className="terms-section">
            <div className="section-icon">
              <Shield size={24} />
            </div>
            <div className="section-content">
              <h2>Intellectual Property Rights</h2>
              <p>
                The service and its original content, features, and
                functionality are and will remain the exclusive property of
                ProSkillNest and its licensors.
              </p>

              <div className="ip-sections">
                <div className="ip-section">
                  <h3>Our Content</h3>
                  <p>
                    All content on ProSkillNest, including but not limited to
                    text, graphics, logos, images, assessments, and software, is
                    protected by copyright, trademark, and other intellectual
                    property laws.
                  </p>
                </div>

                <div className="ip-section">
                  <h3>Limited License</h3>
                  <p>
                    We grant you a limited, non-exclusive, non-transferable
                    license to access and use our service for personal,
                    non-commercial purposes in accordance with these Terms.
                  </p>
                </div>

                <div className="ip-section">
                  <h3>Restrictions</h3>
                  <ul>
                    <li>
                      You may not reproduce, distribute, or create derivative
                      works
                    </li>
                    <li>
                      You may not reverse engineer or decompile our software
                    </li>
                    <li>You may not remove or alter any proprietary notices</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* User Content */}
          <section id="user-content" className="terms-section">
            <div className="section-icon">
              <FileText size={24} />
            </div>
            <div className="section-content">
              <h2>User-Generated Content</h2>
              <p>
                You may have the opportunity to submit content such as profile
                information, feedback, or comments. You retain ownership of your
                content but grant us certain rights.
              </p>

              <div className="content-rights">
                <h3>Content License:</h3>
                <p>
                  By submitting content, you grant ProSkillNest a worldwide,
                  non-exclusive, royalty-free license to use, reproduce, modify,
                  and display your content in connection with our service.
                </p>
              </div>

              <div className="content-standards">
                <h3>Content Standards:</h3>
                <ul>
                  <li>Content must be accurate and not misleading</li>
                  <li>Content must not violate any laws or regulations</li>
                  <li>Content must not infringe on others' rights</li>
                  <li>
                    Content must not contain harmful or offensive material
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payment Terms */}
          <section id="payment-terms" className="terms-section">
            <div className="section-icon">
              <Scale size={24} />
            </div>
            <div className="section-content">
              <h2>Payment Terms</h2>
              <p>
                Some features of our service may require payment. By purchasing
                our services, you agree to the following payment terms.
              </p>

              <div className="payment-policies">
                <div className="payment-policy">
                  <h3>Pricing</h3>
                  <p>
                    Prices for our services are subject to change. We will
                    provide notice of any price changes in advance.
                  </p>
                </div>

                <div className="payment-policy">
                  <h3>Billing</h3>
                  <p>
                    Subscription fees are billed in advance on a recurring
                    basis. You authorize us to charge your payment method for
                    all fees.
                  </p>
                </div>

                <div className="payment-policy">
                  <h3>Refunds</h3>
                  <p>
                    Refunds are handled on a case-by-case basis. Please contact
                    our support team for refund requests.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Privacy */}
          <section id="privacy" className="terms-section">
            <div className="section-icon">
              <Shield size={24} />
            </div>
            <div className="section-content">
              <h2>Privacy</h2>
              <p>
                Your privacy is important to us. Please review our Privacy
                Policy, which also governs your use of the service, to
                understand our practices.
              </p>
              <div className="privacy-link">
                <a href="/privacy" className="btn btn-secondary">
                  Read Privacy Policy
                </a>
              </div>
            </div>
          </section>

          {/* Disclaimers */}
          <section id="disclaimers" className="terms-section">
            <div className="section-icon">
              <AlertTriangle size={24} />
            </div>
            <div className="section-content">
              <h2>Disclaimers</h2>
              <p>
                The information on this service is provided on an "as is" basis.
                To the fullest extent permitted by law, we exclude all
                representations, warranties, and conditions relating to our
                service.
              </p>

              <div className="disclaimer-items">
                <div className="disclaimer-item">
                  <h3>Service Availability</h3>
                  <p>
                    We do not guarantee that our service will be available at
                    all times or free from interruptions, errors, or security
                    vulnerabilities.
                  </p>
                </div>

                <div className="disclaimer-item">
                  <h3>Content Accuracy</h3>
                  <p>
                    While we strive for accuracy, we do not warrant that the
                    content on our service is complete, accurate, or up-to-date.
                  </p>
                </div>

                <div className="disclaimer-item">
                  <h3>Third-Party Links</h3>
                  <p>
                    Our service may contain links to third-party websites. We
                    are not responsible for the content or practices of these
                    external sites.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section id="limitation-liability" className="terms-section">
            <div className="section-icon">
              <XCircle size={24} />
            </div>
            <div className="section-content">
              <h2>Limitation of Liability</h2>
              <p>
                In no event shall ProSkillNest, its directors, employees, or
                agents be liable for any indirect, incidental, special,
                consequential, or punitive damages.
              </p>

              <div className="liability-limits">
                <h3>Liability Limitations:</h3>
                <ul>
                  <li>
                    Our total liability shall not exceed the amount paid by you
                    for our services
                  </li>
                  <li>
                    We are not liable for any loss of profits, data, or business
                    opportunities
                  </li>
                  <li>
                    We are not responsible for damages caused by third parties
                  </li>
                  <li>
                    Some jurisdictions do not allow limitation of liability, so
                    these limits may not apply to you
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Termination */}
          <section id="termination" className="terms-section">
            <div className="section-icon">
              <XCircle size={24} />
            </div>
            <div className="section-content">
              <h2>Termination</h2>
              <p>
                We may terminate or suspend your account and access to our
                service immediately, without prior notice, for conduct that we
                believe violates these Terms.
              </p>

              <div className="termination-conditions">
                <h3>Termination Conditions:</h3>
                <ul>
                  <li>Violation of these Terms and Conditions</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Abuse of our service or other users</li>
                  <li>Non-payment of fees (for paid services)</li>
                </ul>
              </div>

              <div className="post-termination">
                <h3>Effect of Termination:</h3>
                <p>
                  Upon termination, your right to use the service will cease
                  immediately. We may retain certain information as required by
                  law or for legitimate business purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Governing Law */}
          <section id="governing-law" className="terms-section">
            <div className="section-icon">
              <Scale size={24} />
            </div>
            <div className="section-content">
              <h2>Governing Law</h2>
              <p>
                These Terms shall be interpreted and governed by the laws of the
                jurisdiction in which ProSkillNest operates, without regard to
                its conflict of law provisions.
              </p>

              <div className="legal-info">
                <h3>Dispute Resolution:</h3>
                <p>
                  Any disputes arising from these Terms or your use of our
                  service will be resolved through binding arbitration in
                  accordance with the rules of the applicable arbitration
                  association.
                </p>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="terms-section">
            <div className="section-icon">
              <Mail size={24} />
            </div>
            <div className="section-content">
              <h2>Contact Information</h2>
              <p>
                If you have any questions about these Terms and Conditions,
                please contact us:
              </p>

              <div className="contact-info">
                <div className="contact-method">
                  <Mail size={20} />
                  <div>
                    <strong>Email:</strong>
                    <a href="mailto:legal@proskillnest.com">
                      legal@proskillnest.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Terms;
